import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Global translations (common UI, errors, navigation)
// ❌ Do NOT edit vi.json/en.json directly for module-specific translations!
// ✅ Use module-specific locales: src/modules/YourModule/locales/{vi,en}.json
// See: docs/i18n-guidelines.md
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import en from './langs/en.json'
import vi from './langs/vi.json'

// Module-specific translations are NOT listed here: each module registers its
// own namespace in src/modules/<Module>/locales/register.ts, imported from the
// module's index.tsx (or main.tsx for modules without one).

// Component translations
import viDataTable from './langs/data-table/vi.json'
import enDataTable from './langs/data-table/en.json'

// Languages: supported list, stored choice (`lang`), dayjs sync
import { getStoredLanguage, syncLocale } from './languages'

// Axios error translations
import viAxiosError from './locales/axios-error/vi.json'
import enAxiosError from './locales/axios-error/en.json'

// Shared keys consumed via the default `translation` namespace (e.g. loading,
// common employee fields). Merged into `translation` so they resolve without a
// namespace, while keeping langs/{vi,en}.json (guarded against direct edits) untouched.
import enCommon from './locales/common/en.json'
import viCommon from './locales/common/vi.json'

// English for shared `translation` keys that exist in langs/vi.json and not in
// langs/en.json (#131), one file per area. en only: the vi strings stay where
// they are. Spread FIRST so a string that later lands in langs/en.json wins.
// `yarn check:i18n-coverage` measures what is still missing.
import enBackfillKpi from './locales/en-backfill/kpi.json'
import enBackfillServices from './locales/en-backfill/services.json'
import enHoliday from './locales/en-backfill/holiday.json'
import enBackfillGeneral from './locales/en-backfill/general.json'
import enBackfillServiceConfig from './locales/en-backfill/service-config.json'
import enBackfillCustomersTickets from './locales/en-backfill/customers-tickets.json'
import enBackfillFinance from './locales/en-backfill/finance.json'
import enBackfillEmployees from './locales/en-backfill/employees.json'
import enBackfillContracts from './locales/en-backfill/contracts.json'
import enBackfillDocuments from './locales/en-backfill/documents.json'

const resources = {
    en: {
        // scripts/check-i18n-coverage.mjs reads this entry on ONE line.
        // prettier-ignore
        translation: { ...enBackfillKpi, ...enBackfillServices, ...enHoliday, ...enBackfillGeneral, ...enBackfillServiceConfig, ...enBackfillCustomersTickets, ...enBackfillFinance, ...enBackfillEmployees, ...enBackfillContracts, ...enBackfillDocuments, ...en, ...enCommon },
        'data-table': enDataTable,
        'axios-error': enAxiosError,
    },
    vi: {
        translation: { ...vi, ...viCommon },
        'data-table': viDataTable,
        'axios-error': viAxiosError,
    },
}

i18n.use(initReactI18next) // passes i18n down to react-i18next
    .init({
        resources,
        // Stored choice (`lang`, written by the topbar LanguageSwitch), else vi.
        lng: getStoredLanguage(),
        // Module namespaces (settings, employee, ...) fall back to the shared
        // `translation` namespace for common keys (cancel, save, loading, ...).
        fallbackNS: 'translation',
        // 2,423 keys exist in a vi.json and not in the matching en.json.
        // Without a LANGUAGE fallback i18next returns the key itself, so an
        // English user reads `self-assign-message` where a sentence should be. `fallbackNS` above does not cover this --
        // it falls back across namespaces, not across languages.
        //
        // Vietnamese text is not a translation, but it is readable; a raw key is
        // not. Translating those keys is content work tracked in #131, and this
        // line must stay when that lands: it is also what protects every key
        // added to a vi.json before its en.json counterpart exists.
        //
        // Caveat worth knowing before you touch this: a same-namespace hit in
        // the fallback LANGUAGE beats a `fallbackNS` hit in the current one. 27
        // module keys were therefore resolving to shared English and would have
        // flipped to Vietnamese; they are backfilled into the module en.json
        // files in this same change, so the net degradation is zero. Adding a
        // vi-only module key whose name collides with a shared `translation` key
        // re-introduces one.
        fallbackLng: 'vi',
        interpolation: {
            escapeValue: false,
        },
        missingInterpolationHandler(text, value) {
            const message = `Missing translation key [${text}] for ${value}`
            console.warn(message)
        },
    })

// dayjs locale and <html lang> follow every language change, wherever it comes from.
i18n.on('languageChanged', syncLocale)
syncLocale(i18n.language)

export default i18n
